// AI code start — shared QC grid from/to date and approve validation
import { getValidations, getFieldDateFormat, isFromDateAfterToDate } from '../../../common/functions'

export const FROM_TO_DATE_ERROR = 'dataEntry.fromDateToDateError'

export const FROM_TO_DATE_FIELD_PAIRS = [
  ['fromDate', 'toDate'],
  ['fromDate_verify', 'toDate_verify'],
]

export const VALIDATION_ROWS_PER_FRAME = 25

export const SKP_BOX_ID_FIELD = 'skpBoxId'
export const CUSTOMER_ID_FIELD = 'customerId'

export const isFromToDateField = (fieldName) =>
  FROM_TO_DATE_FIELD_PAIRS.some(([fromField, toField]) => fromField === fieldName || toField === fieldName)

const isFromToDatePairInvalid = (rowData, fields, fromFieldName, toFieldName) => {
  const fromValue = rowData[fromFieldName]
  const toValue = rowData[toFieldName]
  if (!fromValue || !toValue) {
    return false
  }
  const fromFormat = getFieldDateFormat(fields, fromFieldName)
  const toFormat = getFieldDateFormat(fields, toFieldName)
  return isFromDateAfterToDate(fromValue, toValue, fromFormat, toFormat)
}

export const filterRowsBySelection = (allRows, selectedRows, getRowId) => {
  if (!selectedRows?.length) {
    return allRows
  }
  const selectedIds = new Set(selectedRows.map((row) => getRowId(row)))
  return allRows.filter((row) => selectedIds.has(getRowId(row)))
}

export const applyFromToDatePairErrors = (
  errors,
  index,
  rowData,
  fromFieldName,
  toFieldName,
  fields,
  jobId,
  contextRows,
  jobDetails,
  regexFormat,
) => {
  const fromField = fields.find((field) => field.name === fromFieldName)
  const toField = fields.find((field) => field.name === toFieldName)
  if (!fromField?.isSelected && !toField?.isSelected) {
    return
  }

  const fromKey = `${index}${fromFieldName}`
  const toKey = `${index}${toFieldName}`

  if (fromField?.isSelected) {
    const fromResult = getValidations(
      fromField,
      rowData,
      jobId,
      contextRows,
      jobDetails?.skpinstance,
      regexFormat,
    )
    if (fromResult?.[0] !== undefined) {
      errors[fromKey] = fromResult[0]
    }
  }

  if (toField?.isSelected) {
    const toResult = getValidations(
      toField,
      rowData,
      jobId,
      contextRows,
      jobDetails?.skpinstance,
      regexFormat,
    )
    if (toResult?.[0] !== undefined) {
      errors[toKey] = toResult[0]
    }
  }

  if (isFromToDatePairInvalid(rowData, fields, fromFieldName, toFieldName)) {
    errors[fromKey] = FROM_TO_DATE_ERROR
    errors[toKey] = FROM_TO_DATE_ERROR
  }
}

export const validateQcGridField = ({
  element,
  rowData,
  fieldKeyList,
  jobId,
  contextRows,
  jobDetails,
  regexFormat,
  translateFn,
}) => {
  const errors = getValidations(
    element,
    rowData,
    jobId,
    contextRows,
    jobDetails?.skpinstance,
    regexFormat,
  )
  if (errors.length >= 1 && errors[0] !== undefined) {
    return { isValid: false, helperText: errors[0] }
  }

  if (isFromToDateField(element.name)) {
    const pair = FROM_TO_DATE_FIELD_PAIRS.find(([fromField, toField]) =>
      fromField === element.name || toField === element.name,
    )
    if (pair) {
      const [fromField, toField] = pair
      if (isFromToDatePairInvalid(rowData, fieldKeyList, fromField, toField)) {
        return {
          isValid: false,
          helperText: translateFn ? translateFn(FROM_TO_DATE_ERROR) : FROM_TO_DATE_ERROR,
        }
      }
    }
  }

  return true
}

export const collectValidationErrorsForApprove = ({
  rowsToValidate,
  allRows,
  fields,
  jobId,
  jobDetails,
  regexFormat,
  getRowId,
  excludedFieldNames = [SKP_BOX_ID_FIELD, CUSTOMER_ID_FIELD],
}) => {
  const errors = {}
  const contextRows = allRows?.length ? allRows : rowsToValidate
  if (!rowsToValidate?.length || !fields?.length) {
    return errors
  }

  const contextRowsWithTableData = contextRows.map((row, index) => ({
    ...row,
    tableData: { id: index },
  }))
  const rowIndexById = getRowId
    ? new Map(contextRowsWithTableData.map((row, index) => [getRowId(row), index]))
    : null
  const rowsToValidateWithTableData = rowsToValidate.map((row, index) => {
    const rowIndex = getRowId ? (rowIndexById.get(getRowId(row)) ?? index) : index
    return {
      ...row,
      tableData: { id: rowIndex },
    }
  })

  fields.forEach((element) => {
    if (!element.isSelected || excludedFieldNames.includes(element.name) || isFromToDateField(element.name)) {
      return
    }
    rowsToValidateWithTableData.forEach((rowData) => {
      const index = rowData.tableData.id
      const validationResult = getValidations(
        element,
        rowData,
        jobId,
        contextRowsWithTableData,
        jobDetails?.skpinstance,
        regexFormat,
      )
      if (validationResult?.length >= 1 && validationResult[0] !== undefined) {
        errors[`${index}${element.name}`] = validationResult[0]
      }
    })
  })

  rowsToValidateWithTableData.forEach((rowData) => {
    const index = rowData.tableData.id
    FROM_TO_DATE_FIELD_PAIRS.forEach(([fromFieldName, toFieldName]) => {
      applyFromToDatePairErrors(
        errors,
        index,
        rowData,
        fromFieldName,
        toFieldName,
        fields,
        jobId,
        contextRowsWithTableData,
        jobDetails,
        regexFormat,
      )
    })
  })

  return errors
}

export const buildValidationErrorsBatched = ({
  rowList,
  fields,
  jobId,
  jobDetails,
  regexFormat,
  onComplete,
  onProgress,
  getRowId,
  excludedFieldNames,
}) => {
  if (!rowList?.length || !fields?.length) {
    onComplete({})
    return () => {}
  }

  let rowIndex = 0
  let accumulatedErrors = {}
  let frameId = null
  let cancelled = false

  const step = () => {
    if (cancelled) {
      return
    }
    const end = Math.min(rowIndex + VALIDATION_ROWS_PER_FRAME, rowList.length)
    const batch = rowList.slice(rowIndex, end)
    const batchErrors = collectValidationErrorsForApprove({
      rowsToValidate: batch,
      allRows: rowList,
      fields,
      jobId,
      jobDetails,
      regexFormat,
      getRowId,
      excludedFieldNames,
    })
    accumulatedErrors = { ...accumulatedErrors, ...batchErrors }
    rowIndex = end
    if (onProgress) {
      onProgress(accumulatedErrors)
    }
    if (rowIndex < rowList.length) {
      frameId = requestAnimationFrame(step)
    } else {
      onComplete(accumulatedErrors)
    }
  }

  frameId = requestAnimationFrame(step)
  return () => {
    cancelled = true
    if (frameId != null) {
      cancelAnimationFrame(frameId)
    }
  }
}
// AI code end
